import { useEffect, useId, useState, type PropsWithChildren, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

export type HubSettingsSectionProps = PropsWithChildren<{
  /** Visible section title (page uses `<h1>`; each section uses `<h2>`). */
  title: ReactNode;
  /** Optional hover hint string; renders `HoverHintWithGlyph` next to the title (same pattern as `CardTitleWithHelp`). */
  hoverHint?: string;
  className?: string;
  /** Optional row below the section body (for example a per-section Save button). */
  footer?: ReactNode;
  /** When true, the section header toggles body visibility. */
  collapsible?: boolean;
  /** Initial collapsed state when `collapsible` is true (default expanded). */
  defaultCollapsed?: boolean;
  /** When set, used as the section `id` and auto-expands when the URL hash matches. */
  sectionId?: string;
}>;

/**
 * Standard panel for hub module settings (MRP, Clamp QC, Manufacturing, and similar).
 * Prefer this over nesting `Card` inside `Card` — one bordered panel per logical group, stacked with `space-y-4`.
 */
export function HubSettingsSection({
  title,
  hoverHint,
  className,
  children,
  footer,
  collapsible = false,
  defaultCollapsed = false,
  sectionId,
}: HubSettingsSectionProps) {
  const contentId = useId();
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  useEffect(() => {
    if (sectionId == null || sectionId === "") return;
    if (window.location.hash === `#${sectionId}`) {
      setExpanded(true);
    }
  }, [sectionId]);

  const showBody = !collapsible || expanded;

  return (
    <section
      id={sectionId}
      className={cn("rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5", className)}
    >
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5",
          showBody ? "mb-4 border-b border-slate-100 pb-3" : null,
        )}
      >
        {collapsible ? (
          <button
            type="button"
            className="inline-flex min-w-0 flex-1 items-center gap-2 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={() => setExpanded((current) => !current)}
          >
            <ChevronDown
              className={cn("h-4 w-4 shrink-0 text-slate-500 transition-transform", expanded && "rotate-180")}
              aria-hidden
            />
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          </button>
        ) : (
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        )}
        {hoverHint != null && hoverHint !== "" ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
      </div>
      {showBody ? (
        <>
          <div id={contentId} className="space-y-4">
            {children}
          </div>
          {footer != null ? (
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">{footer}</div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export type HubSettingsFooterProps = PropsWithChildren<{
  className?: string;
}>;

/**
 * Page-level save row: thin top border, no extra card. Place after the last `HubSettingsSection`.
 */
export function HubSettingsFooter({ className, children }: HubSettingsFooterProps) {
  return <footer className={cn("border-t border-slate-200 pt-4", className)}>{children}</footer>;
}
